import { Alert, Box, Button, GlobalStyles, Stack, styled } from '@mui/material';
import {
  ChannelIdentifiers,
  ConversationChannelType,
  ConversationDraftSideEffectActionType,
  ConversationParticipationMode,
  ConversationStatus,
} from '@provision/conversation-types';
import {
  getAttachmentsFromEditor,
  isFile,
  RichTextEditorFile,
} from '@provision/rich-text-editor';
import React, {
  memo,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import 'react-chat-elements/dist/main.css';
import { useResizable } from 'react-resizable-layout';
import { isNullish } from 'remeda';
import { GLOBAL_TABS_HEIGHT } from 'src/components/GlobalTabs';
import {
  Conversation,
  ConversationDataForSubmission,
  ConversationParticipantItem,
  MacroActionRule,
  SubmitConversationDataParticipant,
  useCurrentUserProfile,
  useGroupRules,
  usePartners,
  UserGroupRules,
} from 'src/graphql';
import { getConversationChannelTypeByParticipation } from 'src/graphql/entities/conversation/conversation.mappings';
import { useConversationDelete } from 'src/graphql/entities/conversation/use-conversation-delete';
import { useRestoreConversation } from 'src/graphql/entities/conversation/use-restore-conversation';
import { useConversationParticipationRecords } from 'src/graphql/entities/conversation-participation/hooks/use-conversation-participation-records';
import { useFileUpload } from 'src/graphql/entities/files-upload';
import { UserPresence } from 'src/graphql/entities/user-presence/user-presence';
import { ClosedInput, InputField } from 'src/pages/Conversations';
import { useChatInputStore } from 'src/store/chat-input';
import { useConfirmationModal, useNotifications } from 'src/utils/hooks';
import { useScreenDimensions } from 'src/utils/system';
import useResizeObserver from 'use-resize-observer';
import { HolidayBanner } from './Banners/holiday-banner';
import { SnoozeBanner } from './Banners/snooze-banner';
import { DraftScheduleSendAndAction } from './conversation-draft-schedule';
import { useSelectedParticipants } from './ParticipationPanel/use-selected-participants';
import ChatThread from './registry/ChatThread';
import { useChatScrollHandler } from './registry/hooks';
import { ChatRegistryProvider } from './registry/provider';
import { useChatAttachmentsSubscription } from './use-chat-attachements-subscription';
import { useConversationTypeOptions } from './use-conversation-type-options';

const ResizeHandle = styled(Box, {
  name: 'ResizeHandle',
})(({ theme }) => ({
  width: '5rem',
  position: 'absolute',
  left: '50%',
  marginLeft: '-2.5rem',
  height: '3px',
  cursor: 'row-resize',
  zIndex: 100,
  '&:after': {
    content: '""',
    position: 'absolute',
    left: 0,
    right: 0,
    height: '1px',
    backgroundColor: theme.palette.custom.OV.sys.outlineVariant,
  },
}));

const MessagesList = styled(Stack, {
  name: 'MessagesList ',
  shouldForwardProp: (prop) => prop !== 'isMobile',
})<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  flex: 1,
  /*
    The conversation header floats over the thread instead of taking layout
    space, so reserve its height (plus a small gap) here — otherwise the
    topmost message sits tucked under the glass at rest.
  */
  paddingTop: `calc(${GLOBAL_TABS_HEIGHT} + ${theme.spacing(1)})`,
  marginBottom: theme.spacing(1),
  overflowAnchor: 'none',
  overflow: 'auto',
  flexDirection: 'column-reverse',
  ...(isMobile
    ? {
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      }
    : {}),
}));

function buildSendAndOverrides(actions?: DraftScheduleSendAndAction[]): {
  statusId?: ConversationStatus;
  snoozeUntil?: string;
} {
  if (!actions?.length) {
    return {};
  }

  let statusId: ConversationStatus | undefined;
  let snoozeUntil: string | undefined;

  actions.forEach((action) => {
    if (action.type === ConversationDraftSideEffectActionType.Status) {
      statusId = action.value as ConversationStatus;
    } else if (action.type === ConversationDraftSideEffectActionType.Snooze && action.value) {
      snoozeUntil = action.value;

      if (!statusId) {
        statusId = ConversationStatus.Snoozed;
      }
    }
  });

  if (statusId && statusId !== ConversationStatus.Snoozed) {
    snoozeUntil = undefined;
  }

  return { statusId, snoozeUntil };
}

const Wrapper = styled(Stack, {
  name: 'Wrapper ',
})(({ theme }) => ({
  height: '100%',
  overflow: 'auto',
  gap: 1,
  '& .editor-content': {
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
    '& .portal-editor': {
      maxHeight: 'none',
      [theme.breakpoints.down('sm')]: {
        fontSize: '1rem',
      },
    },
  },
}));

const CHAT_INPUT_MIN_HEIGHT_PX = 250;

const CHAT_INPUT_MOBILE_MAX_HEIGHT_PERCENT = 80;
const CHAT_INPUT_DESKTOP_MAX_HEIGHT_PERCENT = 90;

interface ChatProps {
  onSubmitConversationData: (input: ConversationDataForSubmission) => Promise<unknown>;
  conversation: Conversation;
  participants: ConversationParticipantItem[];
  userPresenceMap: Map<string, UserPresence>;
}

interface ChatThreadShellProps {
  isMobile: boolean;
  chatRef: RefObject<HTMLDivElement | null>;
  initialPositionDummyRef: RefObject<HTMLDivElement | null>;
}

const ChatThreadShell = ({
  isMobile,
  chatRef,
  initialPositionDummyRef,
}: ChatThreadShellProps): React.JSX.Element => {
  const onScroll = useChatScrollHandler();
  return (
    <MessagesList isMobile={isMobile} onScroll={onScroll} ref={chatRef}>
      <div ref={initialPositionDummyRef} />
      <ChatThread />
    </MessagesList>
  );
};

const ChatComponent = ({
  onSubmitConversationData,
  conversation,
  participants,
  userPresenceMap,
}: ChatProps): React.JSX.Element => {
  const { isMobile } = useScreenDimensions();
  const { partners } = usePartners();
  const { showSuccessAlert } = useNotifications();
  const initialPositionDummyRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement | null>(null);
  const inputContainerRef = useRef<HTMLDivElement | null>(null);

  const chatInputHeight = useChatInputStore.use.chatInputHeight();
  const saveChatInputHeight = useChatInputStore.use.setChatInputHeight();

  useChatAttachmentsSubscription(conversation.conversationId);

  const [chatInputAutoHeight, setChatInputAutoHeight] = useState<boolean>(
    isMobile || !chatInputHeight,
  );

  useEffect(() => {
    // resetting scroll value on switching between conversations
    if (initialPositionDummyRef.current) {
      initialPositionDummyRef.current.scrollIntoView();
    }
  }, [conversation?.conversationId]);

  const { uploadFile } = useFileUpload();
  const [files, setFiles] = useState<RichTextEditorFile[]>([]);

  const submitConversationData = useCallback(
    async ({
      message,
      macros,
      activeChannels,
      participants,
      sendAndActions,
    }: {
      message: { body: string; isPrivate: boolean };
      macros: (MacroActionRule & { isValid: boolean })[];
      activeChannels?: ChannelIdentifiers[];
      participants: SubmitConversationDataParticipant[];
      sendAndActions?: DraftScheduleSendAndAction[];
    }) => {
      if (!conversation) return;
      if (!message && !files.length) return;

      // type guard, as the file can be of type File or ConversationAttachment
      if (files.some((file) => !isFile(file))) return;

      const { statusId: statusOverride, snoozeUntil } = buildSendAndOverrides(sendAndActions);

      const attachments = await getAttachmentsFromEditor({
        files: files as File[],
        attachmentsBucketFolder: `${conversation?.partnerId}/${conversation?.conversationId}`,
        uploadFile,
      });

      const payload: ConversationDataForSubmission = {
        conversation: {
          partnerId: conversation.partnerId,
          subject: conversation.subject,
          type: conversation.type,
          urgency: conversation.urgency,
          workspaceConversationId: conversation.workspaceConversationId,
          requester: conversation.requester,
          ...(statusOverride ? { conversationStatusId: statusOverride } : {}),
          ...(snoozeUntil !== undefined ? { snoozeUntil } : {}),
        },
        message: { ...message, attachments },
        activeChannels,
        ignoreActiveChannelsFor: [
          {
            participationMode: ConversationParticipationMode.Assist,
            channelType: ConversationChannelType.Chat,
          },
        ],
        participants,
      };

      const hasMacrosApplied = macros.length > 0;

      if (hasMacrosApplied) {
        payload.macroActions = JSON.stringify(
          macros.reduce<MacroActionRule[]>(
            (acc, { isValid, id, ...action }) => (isValid ? [...acc, action] : acc),
            [],
          ),
        );
      }

      await onSubmitConversationData(payload);
      if (hasMacrosApplied) {
        showSuccessAlert('Actions with macro are applied ');
      }

      setFiles([]);
    },
    [files, conversation, uploadFile, onSubmitConversationData, showSuccessAlert],
  );

  const { isDisabled: isChatReadOnly, disabledMessageEl } = useDisabledInput(conversation);
  const canViewOriginalEmailOnly =
    isChatReadOnly &&
    (conversation?.conversationStatusId as ConversationStatus) === ConversationStatus.Closed;

  const conversationTypeOptions = useConversationTypeOptions('chat');

  const { ref: chatInputWrapperRef, height: chatInputWrapperObservableHeight } =
    useResizeObserver();

  const {
    position: inputHeight,
    separatorProps,
    isDragging,
    setPosition: setChatInputHeight,
  } = useResizable({
    axis: 'y',
    initial: chatInputHeight || CHAT_INPUT_MIN_HEIGHT_PX,
    min: CHAT_INPUT_MIN_HEIGHT_PX,
    containerRef: inputContainerRef as RefObject<HTMLElement>,
    reverse: true,
    onResizeStart: () => {
      setChatInputAutoHeight(false);
      if (inputContainerRef.current) {
        inputContainerRef.current.style.userSelect = 'none'; // disable text selection
      }
    },
    onResizeEnd: ({ position }) => {
      if (inputContainerRef.current) {
        inputContainerRef.current.style.userSelect = ''; // enable text selection
      }
      saveChatInputHeight(position);
    },
  });

  useEffect(() => {
    // we need to sync the chat input height with the observable height
    // to prevent jumping when the chat input is starting to resize
    if (!isMobile && chatInputAutoHeight && chatInputWrapperObservableHeight) {
      setChatInputHeight(chatInputWrapperObservableHeight);
    }
  }, [isMobile, chatInputAutoHeight, chatInputWrapperObservableHeight, setChatInputHeight]);

  const { user } = useCurrentUserProfile();

  const { conversationParticipations } = useConversationParticipationRecords({
    conversationId: conversation.id,
    includeDeleted: true,
  });

  const selectedParticipantsState = useSelectedParticipants({
    participations: conversationParticipations,
    conversation,
    participants,
    userId: user?.userId,
  });

  const isClosedConversation =
    (conversation.conversationStatusId as ConversationStatus) === ConversationStatus.Closed;

  const { groupRules } = useGroupRules();

  const commonData = useMemo(
    () => ({
      conversationId: conversation.conversationId,
      partnerId: String(conversation.partnerId),
      hasAssociatedAddresses: (conversation.associatedAddresses?.length ?? 0) > 0,
      partners: partners ?? {},
      userPresenceMap,
      conversationParticipations,
      selectedParticipantsState,
      channel: getConversationChannelTypeByParticipation(conversationParticipations),
      isChatReadOnly,
      canViewOriginalEmailOnly,
      groupRules: groupRules as Record<UserGroupRules, boolean>,
      chatRef,
    }),
    [
      conversation.conversationId,
      conversation.partnerId,
      conversation.associatedAddresses,
      partners,
      userPresenceMap,
      conversationParticipations,
      selectedParticipantsState,
      isChatReadOnly,
      canViewOriginalEmailOnly,
      groupRules,
      chatRef,
    ],
  );

  return (
    <Wrapper ref={inputContainerRef}>
      {isMobile ? (
        <GlobalStyles
          styles={{
            'html, body': {
              overscrollBehavior: 'none',
            },
          }}
        />
      ) : null}
      <ChatRegistryProvider commonData={commonData}>
        <ChatThreadShell
          isMobile={isMobile}
          chatRef={chatRef}
          initialPositionDummyRef={initialPositionDummyRef}
        />
      </ChatRegistryProvider>
      <Stack
        ref={chatInputWrapperRef}
        sx={{
          ...(!chatInputAutoHeight &&
            !isMobile &&
            !isClosedConversation &&
            !conversation.isDeleted && {
              height: inputHeight,
            }),
          ...(isDragging && { transition: 'none' }),
          maxHeight: `${isMobile ? CHAT_INPUT_MOBILE_MAX_HEIGHT_PERCENT : CHAT_INPUT_DESKTOP_MAX_HEIGHT_PERCENT}%`,
          position: 'relative',
          minHeight: isMobile ? 0 : 'fit-content',
          '& .editor-content': {
            minHeight: '3rem !important',
            ...(!isMobile && chatInputAutoHeight && { maxHeight: '50vh' }),
          },
        }}
      >
        {!isMobile && !isClosedConversation && !conversation.isDeleted && (
          <ResizeHandle {...separatorProps} />
        )}
        <Stack p={1} flexGrow={1} flexShrink={1} minHeight={0}>
          {!isNullish(conversation.partnerId) && (
            <HolidayBanner partnerId={conversation.partnerId} />
          )}
          <SnoozeBanner conversation={conversation} />
          {isChatReadOnly ? (
            disabledMessageEl
          ) : (
            <InputField
              fieldAutoSize={isMobile || chatInputAutoHeight}
              fieldMinHeight="3rem"
              conversation={conversation}
              participants={participants}
              messageTypeOptions={conversationTypeOptions}
              onSubmit={submitConversationData}
              files={files}
              setFiles={setFiles}
              selectedParticipantsState={selectedParticipantsState}
            />
          )}
        </Stack>
      </Stack>
    </Wrapper>
  );
};

function useDisabledInput(conversation: Conversation): {
  disabledMessageEl: React.JSX.Element | null;
  isDisabled: boolean;
} {
  const { checkValidity } = useGroupRules();
  const { restoreConversation } = useRestoreConversation();
  const { deleteConversation } = useConversationDelete();
  const { confirm } = useConfirmationModal();

  const onRestoreConversationClick = useCallback(async () => {
    await restoreConversation(conversation.conversationId);
  }, [conversation.conversationId, restoreConversation]);

  const onPermanentDeleteConversationClick = useCallback(async () => {
    if (
      await confirm({
        body: 'Please confirm you want to delete the Conversation.',
        variant: 'delete',
      })
    ) {
      await deleteConversation(conversation.conversationId, true);
    }
  }, [confirm, conversation.conversationId, deleteConversation]);

  if (conversation?.isDeleted) {
    const restoreActionButton = checkValidity([
      UserGroupRules.AccessToRestoreDeletedConversations,
    ]) ? (
      <Button color="inherit" size="small" onClick={onRestoreConversationClick}>
        RESTORE
      </Button>
    ) : null;

    const permanentDeleteActionButton = checkValidity([
      UserGroupRules.AccessToPermanentlyDeleteConversations,
    ]) ? (
      <Button color="error" size="small" onClick={onPermanentDeleteConversationClick}>
        PERMANENTLY DELETE
      </Button>
    ) : null;

    return {
      isDisabled: true,
      disabledMessageEl: (
        <Alert
          severity="info"
          action={
            <Stack gap={1} direction="row">
              {restoreActionButton}
              {permanentDeleteActionButton}
            </Stack>
          }
        >
          This conversation was deleted
        </Alert>
      ),
    };
  }
  if (
    (conversation.conversationStatusId as ConversationStatus) === ConversationStatus.Closed
  ) {
    return {
      isDisabled: true,
      disabledMessageEl: <ClosedInput buttonText="This conversation is closed" />,
    };
  }
  if (!checkValidity([UserGroupRules.AccessToUpdateConversation])) {
    return {
      isDisabled: true,
      disabledMessageEl: (
        <ClosedInput buttonText="Read Only Users do not have access to send messages" />
      ),
    };
  }
  return {
    isDisabled: false,
    disabledMessageEl: null,
  };
}

export const Chat = memo(ChatComponent);
